/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// preload.js — runs in the renderer before page content loads and exposes the
// single, narrow window.chiphippo bridge. Every later stage extends THIS
// object (desk.*, boards.*, settings.*, …) and must keep it in lockstep with
// the ipcMain handlers in main.js.
//
// SANDBOX RESTRICTION: this runs in Electron's sandboxed renderer, where
// require() is limited to Electron built-ins ONLY. Never require("../anything")
// here — it crashes the preload in packaged (.asar) builds. Anything from the
// main process must arrive over IPC.
"use strict";

const { contextBridge, ipcRenderer } = require("electron");

// ── Main → renderer menu pushes ─────────────────────────────────────────────
// The application menu's items are one-way pushes from main; re-dispatch each
// as a global chiphippo:* CustomEvent the renderer listens for (the documented
// main→renderer broadcast pattern). Most carry no payload — the renderer opens
// the matching dialog or acts on the desk it already owns. The one exception is
// File ▸ Open Recent, whose item names the project file it stands for; the
// detail is passed through verbatim, so a payload-free push simply arrives with
// `detail: undefined`.
for (const [channel, event] of [
  ["menu:show-about", "chiphippo:show-about"],
  ["menu:open-settings", "chiphippo:open-settings"],
  ["menu:keyboard-shortcuts", "chiphippo:keyboard-shortcuts"],
  ["menu:project-new", "chiphippo:project-new"],
  ["menu:project-open", "chiphippo:project-open"],
  ["menu:project-open-recent", "chiphippo:project-open-recent"],
  ["menu:project-save", "chiphippo:project-save"],
  ["menu:project-save-as", "chiphippo:project-save-as"],
  ["menu:project-properties", "chiphippo:project-properties"],
  ["menu:desktop-add", "chiphippo:desktop-add"],
  ["menu:desktop-duplicate", "chiphippo:desktop-duplicate"],
  ["menu:desktop-import", "chiphippo:desktop-import"],
  ["menu:desktop-export", "chiphippo:desktop-export"],
  ["menu:desktop-export-to", "chiphippo:desktop-export-to"],
  ["menu:desktop-properties", "chiphippo:desktop-properties"],
  ["menu:desktop-delete", "chiphippo:desktop-delete"],
  ["menu:build-guide", "chiphippo:build-guide"],
  ["menu:edit-undo", "chiphippo:edit-undo"],
  ["menu:edit-redo", "chiphippo:edit-redo"],
  ["menu:edit-select-all", "chiphippo:edit-select-all"],
  // Not a menu item: main asking, before a close or a quit, whether anything
  // unsaved needs dealing with first. The renderer answers with closeReply().
  ["app:confirm-close", "chiphippo:confirm-close"],
  // …and taking back a "go" it was given: a confirmed update install that
  // failed to quit leaves the window open, so its autosave starts again.
  ["app:close-aborted", "chiphippo:close-aborted"],
]) {
  ipcRenderer.on(channel, (_e, detail) => {
    window.dispatchEvent(new CustomEvent(event, { detail }));
  });
}

// ── Memory inspector cross-window relay (Feature 190) ───────────────────────
// Unlike the menu pushes above these carry a payload, so re-dispatch the detail
// verbatim. `memory:inbound` reaches an inspector window (host → inspector);
// `memory:host-inbound` reaches the main renderer (inspector → host). A window
// that main never sends a given channel to simply never fires it.
// `ai:delta` / `ai:done` (Feature 260) carry a payload for the same reason:
// a generation streams, so the panel needs each fragment as it arrives rather
// than one resolved promise at the end. Both detail objects carry the
// `requestId` the `ai:start` invoke returned, so a panel can ignore the tail
// of a request it has already cancelled.
// `demo:host-inbound` (Feature 270) is the memory relay one step simpler: a
// pinout window has only a part ref, so its "open this part's example circuit"
// request reaches the app window — the only side with a project to put a
// desktop in — through main, carrying `{ ref }`.
for (const [channel, event] of [
  ["memory:inbound", "chiphippo:memory-inbound"],
  ["memory:host-inbound", "chiphippo:memory-host-inbound"],
  // The chip designer's relay, the memory inspector's one window over:
  // host → designer window, and designer window → host.
  ["chipdesign:inbound", "chiphippo:chipdesign-inbound"],
  ["chipdesign:host-inbound", "chiphippo:chipdesign-host-inbound"],
  ["cpumonitor:inbound", "chiphippo:cpumonitor-inbound"],
  ["cpumonitor:host-inbound", "chiphippo:cpumonitor-host-inbound"],
  // A floated slider's relay, addressed like the memory inspector's (by part
  // and field, `{ compId, key, msg }`): host → window, and window → host.
  ["slider:inbound", "chiphippo:slider-inbound"],
  ["slider:host-inbound", "chiphippo:slider-host-inbound"],
  ["demo:host-inbound", "chiphippo:demo-host-inbound"],
  // A CUSTOM chip's pin-assignments window: what to draw, again, when the
  // design changes (main → that window, `{ ref, chip }`); and its "open the
  // design" button, relayed to the app window as the example's is (`{ kind:
  // "open-designer", ref }`).
  ["pinout:chip", "chiphippo:pinout-chip"],
  ["pinout:host-inbound", "chiphippo:pinout-host-inbound"],
  ["ai:delta", "chiphippo:ai-delta"],
  ["ai:done", "chiphippo:ai-done"],
  // Settings ▸ Data Sheets ▸ Download counts its way through the table one
  // part at a time; a single resolved promise at the end would leave the
  // dialog with nothing to show for the minute it takes.
  ["datasheet:progress", "chiphippo:datasheet-progress"],
  // Auto-update (Feature 280). These are pushes rather than a resolved invoke for a reason
  // the others share: a check can be started from the HELP MENU, which never
  // touches this window, so its outcome cannot come back as anybody's return
  // value. Each carries `{ manual }` — a check the user asked for reports
  // itself, a silent startup one does not nag. `updater:not-available` may add
  // a `reason` ("store-build" / "dev-build"), which is an answer, not a fault.
  ["updater:checking", "chiphippo:updater-checking"],
  ["updater:available", "chiphippo:updater-available"],
  ["updater:not-available", "chiphippo:updater-not-available"],
  ["updater:downloaded", "chiphippo:updater-downloaded"],
  ["updater:error", "chiphippo:updater-error"],
  // An install the user asked for that did not happen (the installer refused,
  // or Squirrel never had the update ready in time): the app stays open.
  ["updater:install-failed", "chiphippo:updater-install-failed"],
  // …and one that has to wait for the update to be ready first (macOS).
  ["updater:preparing", "chiphippo:updater-preparing"],
  // Settings ▸ Appearance ▸ Editor font size, carrying `{ size }`. A push, not
  // something each window polls: the setting is app-wide, and the three
  // auxiliary windows have no settings UI of their own — they only ever follow.
  ["settings:font-size", "chiphippo:font-size-changed"],
  // The Arduino serial integration: an Input's value, a device announcing it
  // has restarted mid-run, a connection lost mid-run, and log text — the last
  // to the connection's log window, and to the app window for its LG lamp.
  // All of them carry the connection's `id`.
  ["serial:inbound", "chiphippo:serial-inbound"],
  ["serial:restart", "chiphippo:serial-restart"],
  ["serial:dropped", "chiphippo:serial-dropped"],
  ["serial:log", "chiphippo:serial-log"],
  // …and the built-in Mock's state (open, connected, armed faults, layout),
  // to its own window.
  ["serial:mock", "chiphippo:serial-mock"],
  // The user guide, already open, asked to turn to a page (`docs.open` below,
  // from another window) — carrying the page's slug.
  ["docs:show", "chiphippo:docs-show"],
]) {
  ipcRenderer.on(channel, (_e, detail) => {
    window.dispatchEvent(new CustomEvent(event, { detail }));
  });
}

contextBridge.exposeInMainWorld("chiphippo", {
  // Static platform info — available synchronously from the sandboxed
  // preload's process shim (main's app:platform handler carries the same
  // value over IPC).
  platform: process.platform,
  arch: process.arch,

  // True under `make debug` / --dev (main passes --chiphippo-dev via
  // additionalArguments). Gates dev-only UI like the desk debug HUD.
  isDev: process.argv.includes("--chiphippo-dev"),

  // App version comes from the main process (package.json), over IPC — this
  // also proves the ipcMain <-> preload bridge is wired correctly.
  getVersion: () => ipcRenderer.invoke("app:version"),
  // The OS's country (ISO 3166 "US", or ""): a new mains brick starts at its
  // mains, and an adapter at its frequency.
  getRegion: () => ipcRenderer.invoke("app:region"),

  // Read-only app / build metadata for the About dialog (version + runtime
  // versions). Distinct from getVersion() — the About panel wants the fuller
  // picture (Electron / Chromium / Node / platform).
  getAppInfo: () => ipcRenderer.invoke("app:info:get"),
  // Open the voluntary donation page in the OS browser (main owns the URL;
  // the About card shows the link only where `getAppInfo().donate` says so).
  openDonatePage: () => ipcRenderer.invoke("app:donate"),

  // The answer to `chiphippo:confirm-close`: true to let the window go, false
  // to stay open. Main waits for this indefinitely — it is a user decision —
  // so every path through the renderer's guard must reply exactly once.
  closeReply: (ok) => ipcRenderer.invoke("app:close-reply", ok === true),
  // Said once the renderer's close handler is registered: until then main has
  // nobody to ask, and lets a close proceed rather than wait on no answer.
  closeReady: () => ipcRenderer.invoke("app:close-ready"),

  // ── App settings (Feature 10) ──────────────────────────────────────────────
  // A single preferences document in main (store/settings-store.js): the desk
  // viewport, window bounds, and later stages' keys. `set` shallow-merges a
  // patch; object-valued keys are replaced whole, so send the full object.
  settings: {
    get: () => ipcRenderer.invoke("settings:get"),
    set: (patch) => ipcRenderer.invoke("settings:set", patch),
    // Settings ▸ Data Sheets: open a native folder picker for the external
    // datasheet-PDF directory. Resolves to the chosen path, or null if
    // cancelled — the caller then persists it with set({ datasheetDir }).
    chooseDatasheetDir: () =>
      ipcRenderer.invoke("settings:choose-datasheet-dir"),
  },

  // ── Language ───────────────────────────────────────────────────────────────
  // `load()` resolves the ACTIVE message catalog (the persisted preference, else
  // the OS locale, else English) plus the English catalog as a fallback. Every
  // window calls it once, before it renders anything: a renderer is sandboxed,
  // served from file://, and its CSP has no `connect-src`, so it cannot read or
  // fetch a catalog itself — main is the only route, exactly as it is for every
  // other file read. The payload also carries `locales` — the languages a
  // catalog actually ships for, from the same table the reader resolves against
  // — so the Settings picker needs no second call and can never offer a
  // language with nothing behind it.
  i18n: {
    load: () => ipcRenderer.invoke("i18n:load"),
  },

  // ── AI circuit builder (Feature 260) ───────────────────────────────────────
  // The user's own connection. Non-secret config (provider, base URL, model)
  // lives in settings; the KEY does not, and never crosses back over this
  // bridge — `key.status` answers whether one is configured and nothing else.
  // A generation streams: `start` resolves with a request id, then
  // `chiphippo:ai-delta` fires per fragment and `chiphippo:ai-done` once.
  ai: {
    providers: () => ipcRenderer.invoke("ai:providers"),
    key: {
      set: (providerId, key) =>
        ipcRenderer.invoke("ai:key:set", providerId, key),
      clear: (providerId) => ipcRenderer.invoke("ai:key:clear", providerId),
      status: (providerId) => ipcRenderer.invoke("ai:key:status", providerId),
    },
    test: (config) => ipcRenderer.invoke("ai:test", config),
    // `opts` is `{ format: "prose" }` for the desk review (Feature 320) and
    // absent for a netlist build.
    start: (config, system, messages, opts) =>
      ipcRenderer.invoke("ai:start", config, system, messages, opts),
    cancel: (requestId) => ipcRenderer.invoke("ai:cancel", requestId),
  },

  // ── Auto-update (Feature 280) ──────────────────────────────────────────────
  // `check` asks for an explicit check (Settings ▸ About's button; the Help
  // menu's item runs the same check in main). `install` quits and installs an
  // ALREADY-DOWNLOADED update — user-clicked only, and it still goes through
  // the ordinary quit guard, so an unsaved project is asked about first.
  // Neither resolves with anything: the outcome arrives as the
  // `chiphippo:updater-*` events re-dispatched above. Both are inert in a
  // store build and in `make debug`, which report why rather than failing.
  updater: {
    check: () => ipcRenderer.invoke("updater:check"),
    install: () => ipcRenderer.invoke("updater:install"),
  },

  // ── Projects ───────────────────────────────────────────────────────────────
  // THE PROJECT IS THE DOCUMENT: one file holds every desktop's document and
  // every programmed ROM's bytes, so this is the app's only file surface.
  // `boot()` answers with a project on every launch (the unsaved one in the
  // app's saves folder, else the most recent saved one, else a brand-new one).
  // The renderer holds the whole project — name, description, location, tabs,
  // and each tab's `doc` — and hands it back to be written; main owns the
  // saves folder, the native dialogs, the recent list, the ROM images, and
  // which paths may be touched at all (the saves folder, plus whatever a
  // dialog or an opened project established).
  //
  // `save(meta, path)` writes the project file: a null path means "no location
  // yet" → the fixed default project file. A save to a real path always empties
  // that slot, since the slot only ever holds what a project's own file does
  // not. `choosePath` only PICKS a location — the native save dialog asks about
  // replacing an existing file itself, so a refusal comes back as a cancel
  // (null).
  //
  // `recovery.*` is the slot's other job: `write(meta, location)` stashes the
  // open project there WITHOUT touching its own file, stamped with the path it
  // belongs to, so a stamped slot at startup means the last session did not
  // finish; `clear()` throws it away once a save has put everything in the file,
  // or a clean quit has made it moot. There is no `restore` — `boot` hands one
  // back already restored, flagged `restored` so it reads as unsaved.
  project: {
    boot: () => ipcRenderer.invoke("project:boot"),
    create: () => ipcRenderer.invoke("project:new"),
    open: () => ipcRenderer.invoke("project:open"),
    openRecent: (filePath) =>
      ipcRenderer.invoke("project:open-recent", filePath),
    // `openRecent` answering `denied` means the file is there but the sandbox
    // will not open it — a Save-As bookmark goes stale on the next launch
    // (electron/electron#32544). `regrant` re-asks for it through an OPEN panel,
    // whose bookmark survives, and opens it. Same MRU allowlist as `openRecent`;
    // resolves `{ok:false, code:"cancelled"|"mismatch"}` when the user declines
    // or picks a different file.
    regrant: (filePath) => ipcRenderer.invoke("project:regrant", filePath),
    save: (meta, filePath) =>
      ipcRenderer.invoke("project:save", meta, filePath),
    recovery: {
      write: (meta, location) =>
        ipcRenderer.invoke("project:recovery:write", meta, location),
      clear: () => ipcRenderer.invoke("project:recovery:clear"),
    },
    choosePath: (kind, name, current) =>
      ipcRenderer.invoke("project:choose-path", kind, name, current),
    // The MRU list behind File ▸ Open Recent: `list()` → the last 10 project
    // paths, most recent first; `remove(path)` → the new list. Opening one is
    // `openRecent` above (main allowlists against this list).
    recent: {
      list: () => ipcRenderer.invoke("project:recent:list"),
      remove: (filePath) =>
        ipcRenderer.invoke("project:recent:remove", filePath),
    },
    // A tab switch swaps the whole desk — ask main to close the pinout /
    // memory windows that were pointing at the desk being left behind.
    closeAuxWindows: () => ipcRenderer.invoke("project:closed-aux"),
  },

  // ── Desktop snapshots (Export / Import / Duplicate) ────────────────────────
  // A desktop is structure inside the project file, not a file of its own, so
  // moving one between projects is a SNAPSHOT: `export` picks a path and
  // writes a self-contained `.desktop.chiphippo` (the document plus every
  // programmed ROM's bytes); `import` reads one back. Both COPIES — import and
  // duplicate — come back with freshly minted ROM guids and their own backing
  // files, so no two chips can ever share one. Each resolves to null when its
  // dialog was cancelled.
  desktop: {
    export: (desktop) => ipcRenderer.invoke("desktop:export", desktop),
    // Export To (Feature 390): another tool's format. The renderer hands over
    // the FILES (name + text) its exporter produced; main asks where and
    // writes them, resolving `{ok, path}`, `{ok:false, code}`, or null when
    // the dialog was cancelled.
    exportTo: (format, files) =>
      ipcRenderer.invoke("desktop:export-to", format, files),
    import: () => ipcRenderer.invoke("desktop:import"),
    // `images` (guid → base64) only from a bundled example, whose ROM's
    // program arrives with it; a plain Duplicate copies from the cache.
    duplicate: (doc, images) =>
      ipcRenderer.invoke("desktop:duplicate", doc, images),
  },

  // ── Chip pin-assignments window (Feature 100) ──────────────────────────────
  // A part's "Pin Assignment" context-menu item opens a separate, floating OS
  // window that renders its DIP pinout as a wiring reference. `opts` may carry
  // a `{ pins }` hint so main can size the window to the package.
  openPinout: (ref, opts) => ipcRenderer.invoke("pinout:open", ref, opts),

  // A CUSTOM chip's pinout window, which no catalog it loads can draw: the
  // window asks main for what the app window said of it (`chip`); the app
  // window tells main whenever designs change (`updateChips`, `[{ ref, chip
  // | null }]`); and the window's designer button asks for the design to be
  // opened (`openDesigner`).
  pinout: {
    chip: (ref) => ipcRenderer.invoke("pinout:chip", ref),
    updateChips: (changes) =>
      ipcRenderer.invoke("pinout:update-chips", changes),
    openDesigner: (ref) => ipcRenderer.invoke("pinout:open-designer", ref),
  },

  // Open a part's external datasheet PDF (from the Settings ▸ Data Sheets
  // folder) in the OS PDF viewer. Used by the pinout window's "open datasheet"
  // button. Resolves to whether a file was opened.
  openDatasheet: (ref) => ipcRenderer.invoke("datasheet:open", ref),

  // ── Downloading the datasheets ─────────────────────────────────────────────
  // Settings ▸ Data Sheets ▸ Download. `download()` fetches every datasheet
  // main's hard-coded table covers into the app's own folder and resolves with
  // `{ dir, total, saved, cancelled, failures }` — the caller then persists
  // `dir` as `datasheetDir` like any other setting. No URL and no path crosses
  // this way: the renderer asks for "the datasheets" and is told where they
  // went. Progress arrives meanwhile as `chiphippo:datasheet-progress` events
  // carrying `{ done, total, ref, ok }`; `cancel()` stops the run in flight.
  datasheets: {
    download: () => ipcRenderer.invoke("datasheet:download"),
    cancel: () => ipcRenderer.invoke("datasheet:download:cancel"),
  },

  // ── Example circuits (Feature 270) ─────────────────────────────────────────
  // The demonstration bench `make demos` builds for every benchable 74xx part
  // (or a hand-built example's several desktops — the 555's modes), shipped as
  // web/demos/<ref>.json. Two windows, one action: a PINOUT window
  // asks (`open` — it has a ref and nothing else, so main relays the request to
  // the app window as `demo:host-inbound`), and the APP window reads (`read`)
  // the document it is going to make a desktop of. `read` answers null for a
  // part with no example.
  demo: {
    open: (ref) => ipcRenderer.invoke("demo:open", ref),
    read: (ref) => ipcRenderer.invoke("demo:read", ref),
  },

  // ── Memory backing files (Features 180/190) ────────────────────────────────
  // The GUID-keyed byte store behind a non-volatile (ROM/EPROM/EEPROM) chip's
  // `.bin` in the app working folder. All I/O is atomic in main, which alone
  // maps a GUID to a path. `create` makes a fresh noise-filled file (chip
  // added); `load` reads it; `program` copies a picked image to the file's
  // start (the external programmer); `write` overwrites (inspector Save);
  // `delete` removes it (chip deleted); `path` resolves the display path.
  // `pickImage`/`export` open native dialogs. Each resolves to { ok, ... }.
  mem: {
    create: (guid, byteLength) =>
      ipcRenderer.invoke("mem:create", guid, byteLength),
    load: (guid, byteLength) =>
      ipcRenderer.invoke("mem:load", guid, byteLength),
    program: (guid, bytes, byteLength) =>
      ipcRenderer.invoke("mem:program", guid, bytes, byteLength),
    write: (guid, bytes) => ipcRenderer.invoke("mem:write", guid, bytes),
    delete: (guid) => ipcRenderer.invoke("mem:delete", guid),
    path: (guid) => ipcRenderer.invoke("mem:path", guid),
    pickImage: () => ipcRenderer.invoke("mem:pick-image"),
    export: (bytes, suggestedName) =>
      ipcRenderer.invoke("mem:export", bytes, suggestedName),
  },

  // ── Memory inspector window (Feature 190) ──────────────────────────────────
  // `open` spawns/focuses the per-component floating inspector. The two relays
  // are the ONLY channel between the main renderer and an inspector window
  // (each its own sandboxed renderer): `toInspector` sends host → inspector,
  // `toHost` sends inspector → host, both addressed by component id. Inbound
  // messages arrive as `chiphippo:memory-inbound` / `-host-inbound` events.
  memory: {
    open: (compId, ref) => ipcRenderer.invoke("memory:open", compId, ref),
    toInspector: (compId, msg) =>
      ipcRenderer.invoke("memory:to-inspector", compId, msg),
    toHost: (compId, msg) => ipcRenderer.invoke("memory:to-host", compId, msg),
  },

  // ── Chip designer window (custom chips) ────────────────────────────────────
  // `open` spawns/focuses the one designer/debugger window. The two relays are
  // the only channel between it and the main renderer: `toWindow` host →
  // designer, `toHost` designer → host (main checks each comes from the window
  // it claims to). Inbound messages arrive as `chiphippo:chipdesign-inbound` /
  // `-host-inbound` events.
  chipDesign: {
    open: () => ipcRenderer.invoke("chipdesign:open"),
    toWindow: (msg) => ipcRenderer.invoke("chipdesign:to-window", msg),
    toHost: (msg) => ipcRenderer.invoke("chipdesign:to-host", msg),
  },

  // ── CPU monitor window ──────────────────────────────────────────────────────
  // `open` spawns/focuses the one read-only monitor window; `toWindow` host →
  // monitor, `toHost` monitor → host (main checks each sender). Inbound
  // messages arrive as `chiphippo:cpumonitor-inbound` / `-host-inbound`.
  cpuMonitor: {
    open: () => ipcRenderer.invoke("cpumonitor:open"),
    toWindow: (msg) => ipcRenderer.invoke("cpumonitor:to-window", msg),
    toHost: (msg) => ipcRenderer.invoke("cpumonitor:to-host", msg),
  },

  // ── Floated slider windows ─────────────────────────────────────────────────
  // One small window per part and field (a pot's Position), floating above
  // the app: `open` spawns/focuses it (`opts`: its title, and where the part
  // and circuit are in this window, so it opens off them), `close` shuts it,
  // `toWindow` host → window, `toHost` window → host (main checks each
  // sender). Inbound messages arrive as `chiphippo:slider-inbound` /
  // `-host-inbound`, each `{ compId, key, msg }`.
  slider: {
    open: (compId, key, opts) =>
      ipcRenderer.invoke("slider:open", compId, key, opts),
    close: (compId, key) => ipcRenderer.invoke("slider:close", compId, key),
    toWindow: (compId, key, msg) =>
      ipcRenderer.invoke("slider:to-window", compId, key, msg),
    toHost: (compId, key, msg) =>
      ipcRenderer.invoke("slider:to-host", compId, key, msg),
  },

  // ── The chip library (custom chips) ────────────────────────────────────────
  // The machine's designed chips, which every project can place: `list` reads
  // them, `put` adds or replaces chips by id, `remove` drops one. The app
  // window's only (main refuses any other sender).
  chipLibrary: {
    list: () => ipcRenderer.invoke("chip-library:list"),
    put: (chips) => ipcRenderer.invoke("chip-library:put", chips),
    remove: (id) => ipcRenderer.invoke("chip-library:remove", id),
  },

  // ── Native menu state ──────────────────────────────────────────────────────
  // The renderer owns what these items act on, so it is the authority on
  // whether they apply: the document history behind Edit ▸ Undo / Redo
  // (Feature 200), and the tab set + run lock behind Desktop ▸ Duplicate /
  // Delete — which the tab strip already disables, so this is what keeps the
  // two surfaces saying the same thing.
  menu: {
    setEditState: (state) => ipcRenderer.invoke("menu:edit-state", state),
    setDesktopState: (state) => ipcRenderer.invoke("menu:desktop-state", state),
  },

  // ── Arduino serial integration ─────────────────────────────────────────────
  // A run names CONNECTION IDS, never ports or paths — main reads which device
  // each one is from its own settings. `open([{id, signature}])` opens them
  // and greets every device, holding it to the layout signature, resolving
  // `{ok}` or `{ok:false, id, code}`; `send` delivers one Output frame and
  // resolves once it is ACKed (or given up on — `{ok:false, code:"delivery"}`);
  // `close` is Stop. The log calls are the log window's (`open` is also the LG
  // lamp's and Settings' Log… button).
  serial: {
    ports: () => ipcRenderer.invoke("serial:ports"),
    open: (requests) => ipcRenderer.invoke("serial:open", requests),
    close: () => ipcRenderer.invoke("serial:close"),
    send: (id, index, width, value) =>
      ipcRenderer.invoke("serial:send", id, index, width, value),
    // A connection window: open it, read its stream (and remembered view),
    // clear it, remember its filters and timestamps, Save… its text, and say
    // how narrow its footer lets it go (main sizes the window that asks).
    log: {
      open: (id, opts) => ipcRenderer.invoke("serial:log:open", id, opts),
      read: (id) => ipcRenderer.invoke("serial:log:read", id),
      clear: (id) => ipcRenderer.invoke("serial:log:clear", id),
      prefs: (id, view) => ipcRenderer.invoke("serial:log:prefs", id, view),
      save: (id, text) => ipcRenderer.invoke("serial:log:save", id, text),
      minWidth: (width) => ipcRenderer.invoke("serial:log:min-width", width),
    },
    // The built-in Mock's window: send an Input's value, log text, and arm
    // (or disarm) a one-shot fault. Each is the Mock DEVICE acting — the
    // bytes go through the same link a real board's do.
    mock: {
      send: (index, width, value) =>
        ipcRenderer.invoke("serial:mock:send", index, width, value),
      log: (text) => ipcRenderer.invoke("serial:mock:log", text),
      fault: (fault, on) => ipcRenderer.invoke("serial:mock:fault", fault, on),
    },
  },

  // Generate's Save As…: write one generated file (a header, a module, an
  // example program) where the board's code is. `scope` names the design it
  // was generated from, so the panel opens where THIS file was saved last.
  // Resolves `{ok, path}` / `{ok: false, error}`, or null when the panel was
  // cancelled.
  integration: {
    saveFile: (connectionId, scope, name, text) =>
      ipcRenderer.invoke(
        "integration:save-file",
        connectionId,
        scope,
        name,
        text,
      ),
  },

  // ── User guide (Feature 230) ────────────────────────────────────────────────
  // Help ▸ Chip Hippo User Guide opens its own OS window directly from the
  // native menu (main's openDocsWindow). `read` is the docs window's own: once
  // open, it fetches one Markdown page's source at a time by slug (never a
  // filesystem path, never fetch(), so it works under file://). `open` is the
  // app window's: it opens (or turns) the guide to one page by slug — the
  // Serial Protocol reference Settings ▸ Serial I/O and the Generate card
  // offer. Shared with the docs window, same as every other auxiliary window
  // (pinout/memory) — Chip Hippo has one bridge, not a narrow preload per
  // window.
  docs: {
    read: (slug) => ipcRenderer.invoke("docs:read", slug),
    open: (slug) => ipcRenderer.invoke("docs:open", slug),
  },
});
